// API client for weern-datoric-backend.
//
// Every response is parsed against a @weern/contracts schema (ADR-0026): the
// worker and this app share one runtime source, so a renamed field fails loudly
// here instead of rendering an empty column. Token lives in localStorage — no
// cookies, which is why the worker's CORS can stay a plain wildcard.
import type { ZodType } from 'zod';
import {
  DatoricAddContributorsResSchema,
  DatoricAdminStatsResSchema,
  DatoricAssignResSchema,
  DatoricAuthRequestResSchema,
  DatoricAuthVerifyResSchema,
  DatoricContributorResSchema,
  DatoricCreateStaffResSchema,
  DatoricLoginResSchema,
  DatoricMeResSchema,
  DatoricMyStatsResSchema,
  DatoricAddPaymentResSchema,
  DatoricBulkPaymentResSchema,
  DatoricSetPaidResSchema,
  DatoricOkChangedResSchema,
  DatoricPaymentsResSchema,
  DatoricPoolResSchema,
  DatoricStaffListResSchema,
  DatoricSummaryResSchema,
  DatoricSyncStatusResSchema,
  DatoricUnassignedResSchema,
  type DatoricAddContributorsRes,
  type DatoricAdminStatsRow,
  type DatoricContributorRes,
  type DatoricMeRes,
  type DatoricMyStatsRes,
  type DatoricStaffMe,
  type DatoricPoolRes,
  type DatoricPaymentRow,
  type DatoricPoolRow,
  type DatoricStaffListRes,
  type DatoricSummaryRes,
  type DatoricUnassignedRes,
} from '@weern/contracts';

// Ключ переименован в main в проектный `VITE_DATORIC_API_BASE`: общий на все
// приложения `VITE_API_BASE` неоднозначен, когда фронтов в репозитории семь.
// Старое имя оставлено запасным — иначе локальный dev у того, кто держит его в
// своём окружении, тихо ушёл бы на localhost.
//
// Промах здесь стоит дорого и молча: бандл с пустой базой стучится на свой же
// домен, получает index.html с кодом 200 на каждый запрос, и кабинет выглядит
// пустым без единой ошибки (так умирал work-web 20.08).
const BASE =
  (import.meta.env.VITE_DATORIC_API_BASE as string | undefined) ??
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  'http://localhost:8787';
const TOKEN_KEY = 'datoric_session';

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    // The parsed error body — domain errors carry context beyond the code
    // (e.g. rate_above_admin returns maxCents so the UI can name the ceiling).
    readonly body: Record<string, unknown> | null = null,
  ) {
    super(code);
  }
}

/** A contract mismatch is a bug, not a user-facing state — throw and let the
 *  screen's error path show its generic message. */
export class ApiContractError extends Error {
  constructor(readonly route: string) {
    super(`api contract mismatch: ${route}`);
  }
}

function parseRes<T>(schema: ZodType<T>, data: unknown, route: string): T {
  const r = schema.safeParse(data);
  if (!r.success) {
    // Issue paths only — response bodies carry contributor PII.
    const issues = r.error.issues.map((i) => `${i.path.join('.')}:${i.code}`).join(', ');
    console.warn(`[contract] ${route} drift: ${issues}`);
    throw new ApiContractError(route);
  }
  return r.data;
}

async function call(path: string, init?: RequestInit): Promise<unknown> {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE}/api/datoric${path}`, { ...init, headers });
  if (res.status === 401) {
    // Session died (expired, account disabled). Drop it and land on /login
    // instead of every screen handling 401 separately.
    clearToken();
    location.hash = '#/login';
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    throw new ApiError(res.status, (body?.error as string) ?? `http_${res.status}`, body);
  }
  return res.json();
}

const get = async <T>(path: string, schema: ZodType<T>): Promise<T> =>
  parseRes(schema, await call(path), `GET ${path}`);

const send = async <T>(
  method: 'POST' | 'PATCH',
  path: string,
  body: unknown,
  schema: ZodType<T>,
): Promise<T> =>
  parseRes(schema, await call(path, { method, body: JSON.stringify(body) }), `${method} ${path}`);

export const api = {
  me: () => get('/auth/me', DatoricMeResSchema),
  myStats: () => get('/my/stats', DatoricMyStatsResSchema),
  login: (email: string, password: string) =>
    send('POST', '/auth/login', { email, password }, DatoricLoginResSchema),
  requestLink: (email: string) =>
    send('POST', '/auth/request', { email }, DatoricAuthRequestResSchema),
  verify: (token: string) => send('POST', '/auth/verify', { token }, DatoricAuthVerifyResSchema),
  pool: () => get('/pool', DatoricPoolResSchema),
  summary: () => get('/stats/summary', DatoricSummaryResSchema),
  contributor: (id: string) => get(`/contributor/${id}`, DatoricContributorResSchema),
  patchContributor: (id: string, body: unknown) =>
    send('PATCH', `/contributor/${id}`, body, DatoricOkChangedResSchema),
  /** Одна выплата оператору. Накопленную сумму складывает сервер. */
  addPayment: (id: string, amountCents: number, note?: string) =>
    send('POST', `/contributor/${id}/payment`, { amountCents, note }, DatoricAddPaymentResSchema),
  payments: (id: string) => get(`/contributor/${id}/payments`, DatoricPaymentsResSchema),
  /** Способ 1: менеджер вписывает накопленную сумму, в реестр идёт разница. */
  setPaid: (id: string, paidCents: number) =>
    send('POST', `/contributor/${id}/paid`, { paidCents }, DatoricSetPaidResSchema),
  /** Способ 2: закрыть долг всем, кому должны, одной кнопкой. */
  payBulk: (ids?: string[]) =>
    send('POST', '/payments/bulk', ids ? { ids } : {}, DatoricBulkPaymentResSchema),
  addContributors: (body: unknown) =>
    send('POST', '/contributors', body, DatoricAddContributorsResSchema),
  assign: (contributorId: string, userId: string) =>
    send('POST', '/assign', { contributorId, userId }, DatoricAssignResSchema),
  unassigned: () => get('/unassigned', DatoricUnassignedResSchema),
  staff: () => get('/staff', DatoricStaffListResSchema),
  createStaff: (body: unknown) => send('POST', '/staff', body, DatoricCreateStaffResSchema),
  patchStaff: (id: string, body: unknown) =>
    send('PATCH', `/staff/${id}`, body, DatoricOkChangedResSchema),
  adminStats: () => get('/stats/admins', DatoricAdminStatsResSchema),
  syncStatus: () => get('/sync/status', DatoricSyncStatusResSchema),
};

export type {
  DatoricAddContributorsRes,
  DatoricAdminStatsRow,
  DatoricContributorRes,
  DatoricMeRes,
  DatoricMyStatsRes,
  DatoricPoolRes,
  DatoricPaymentRow,
  DatoricPoolRow,
  DatoricStaffListRes,
  DatoricStaffMe,
  DatoricSummaryRes,
  DatoricUnassignedRes,
};
